import { BulkMenu, ExportViewButton, FilterButton, ViewTabs, customColumns, useProperties, useRecordList } from './records';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EnrollDialog } from '@/features/sales/record-tools';
import { Sparkles, Upload, UserPlus, Workflow } from 'lucide-react';
import { DataTable, type Column } from '@/components/shared/data-table';
import { ObjectIndex, FilterPill, AddButton } from '@/components/shared/object-index';
import { BulkDelete, BulkExport } from '@/components/shared/bulk';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, Select } from '@/components/ui/misc';
import { useSession } from '@/features/auth/use-session';
import { useDebounce } from '@/hooks/use-debounce';
import { usePermission } from '@/hooks/use-permission';
import { formatMoney, timeAgo } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useBulkLeads, useDeleteLead, useLeads } from './api';
import { LEAD_SOURCE_LABELS, LEAD_STATUS_LABELS, LEAD_STATUS_VARIANT, scoreBand } from './labels';
import { LeadDialog } from './lead-dialog';
import { ConvertLeadDialog } from './convert-lead-dialog';
import type { Lead, LeadStatus, LeadSource } from './types';

const STATUSES = Object.keys(LEAD_STATUS_LABELS) as LeadStatus[];
const SOURCES = Object.keys(LEAD_SOURCE_LABELS) as LeadSource[];

export function LeadsPage() {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermission();
  const { data: me } = useSession();
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [enrolling, setEnrolling] = useState<string[] | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);
  const list = useRecordList(
    'lead',
    [
      { key: 'all', label: 'All leads' },
      { key: 'mine', label: 'My leads', ownerId: 'me' },
      { key: 'unassigned', label: 'Unassigned leads', ownerId: 'none' },
      { key: 'new', label: 'New', filters: [{ field: 'status', op: 'in', value: ['NEW'] }] },
      { key: 'qualified', label: 'Qualified', filters: [{ field: 'status', op: 'in', value: ['QUALIFIED'] }] },
    ],
    me?.id,
  );
  const { data: properties = [] } = useProperties('lead');

  const debounced = useDebounce(list.search);
  const bulk = useBulkLeads();
  const remove = useDeleteLead();

  const { data, isLoading, error, refetch } = useLeads({
    ...list.params,
    q: debounced || undefined,
    status: status || undefined,
    source: source || undefined,
  });
  const rows = data?.data ?? [];

  const columns: Column<Lead>[] = [
    {
      key: 'name',
      header: 'Lead',
      sortKey: 'first_name',
      cell: (row) => (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={row.name} size={30} />
          <div className="min-w-0">
            <div className="text-link truncate font-semibold hover:underline">{row.name}</div>
            <div className="truncate text-xs text-muted-foreground">{row.companyName ?? row.number}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'email',
      header: 'Email',
      className: 'text-sm',
      cell: (row) => row.email ?? <span className="text-muted-foreground">--</span>,
    },
    {
      key: 'phone',
      header: 'Phone number',
      className: 'text-sm',
      cell: (row) => (row.phone ? <span dir="ltr">{row.phone}</span> : <span className="text-muted-foreground">--</span>),
    },
    {
      key: 'status',
      header: 'Lead status',
      sortKey: 'status',
      cell: (row) => <Badge variant={LEAD_STATUS_VARIANT[row.status]}>{LEAD_STATUS_LABELS[row.status]}</Badge>,
    },
    {
      key: 'score',
      header: 'Score',
      sortKey: 'score',
      cell: (row) => {
        const band = scoreBand(row.score);
        return (
          <span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', band.className)} title={`${row.score} / 100`}>
            {band.label} · {row.score}
          </span>
        );
      },
    },
    {
      key: 'source',
      header: 'Source',
      className: 'text-sm text-muted-foreground',
      cell: (row) => LEAD_SOURCE_LABELS[row.source],
    },
    {
      key: 'budget',
      header: 'Budget',
      className: 'tabular text-sm',
      cell: (row) => formatMoney(row.budget, row.currency),
    },
    {
      key: 'owner',
      header: 'Lead owner',
      className: 'text-sm',
      cell: (row) => row.owner?.name ?? <span className="text-muted-foreground">{t('No owner')}</span>,
    },
    {
      key: 'created',
      header: 'Create date',
      sortKey: 'created_at',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => timeAgo(row.createdAt),
    },
    {
      key: 'actions',
      header: 'Convert',
      cell: (row) =>
        row.status === 'CONVERTED' ? (
          <Link
            to={`/crm/deals/${row.convertedDealId ?? ''}`}
            onClick={(event) => event.stopPropagation()}
            className="text-link text-xs font-semibold hover:underline"
          >
            {t('View deal')}
          </Link>
        ) : (
          <Can permission="crm.lead.update">
            <button
              onClick={(event) => {
                event.stopPropagation();
                setConverting(row);
              }}
              className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold hover:border-primary/50 hover:text-primary"
            >
              <Sparkles className="h-3 w-3" />
              {t('Convert')}
            </button>
          </Can>
        ),
    },
    ...customColumns<Lead>(properties),
  ];

  return (
    <div>
      <ObjectIndex
        title="Leads"
        count={data?.meta?.total}
        viewsNode={<ViewTabs type="lead" list={list} />}
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder="Search name, company, email, phone"
        filters={
          <>
            <FilterPill
              label="Lead status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                list.setPage(1);
              }}
              options={STATUSES.map((value) => ({ value, label: LEAD_STATUS_LABELS[value] }))}
            />
            <FilterPill
              label="Source"
              value={source}
              onChange={(value) => {
                setSource(value);
                list.setPage(1);
              }}
              options={SOURCES.map((value) => ({ value, label: LEAD_SOURCE_LABELS[value] }))}
            />
            <FilterButton type="lead" list={list} />
          </>
        }
        hasFilters={Boolean(status || source || list.extra.conditions.length)}
        onClearFilters={() => {
          setStatus('');
          setSource('');
          list.setExtra({ match: 'ALL', conditions: [] });
        }}
        toolbarEnd={<ExportViewButton type="lead" list={list} />}
        mode="table"
        tableHref="/crm/leads"
        boardHref="/crm/leads/board"
        actions={
          <>
            <Can permission="crm.import.create">
              <Button asChild variant="outline">
                <Link to="/sales/import?type=lead">
                  <Upload className="h-4 w-4" />
                  {t('Import')}
                </Link>
              </Button>
            </Can>
            <Can permission="crm.lead.create">
              <AddButton onClick={() => setCreating(true)} label="Add lead" />
            </Can>
          </>
        }
      >
        <DataTable
          bare
          storageKey="leads"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error ? 'Could not load leads.' : null}
          onRetry={() => void refetch()}
          meta={data?.meta}
          onPageChange={list.setPage}
          onLimitChange={list.setLimit}
          onRowClick={(row) => navigate(`/crm/leads/${row.id}`)}
          sort={list.sort}
          onSortChange={list.setSort}
          bulkActions={(ids, clear) => (
            <>
              <BulkMenu type="lead" ids={ids} onDone={clear} />
              {can('crm.lead.update') && (
                <Select
                  className="h-7 w-auto text-xs font-semibold"
                  value=""
                  onChange={(event) => {
                    const value = event.target.value;
                    if (!value) return;
                    bulk.mutate({ ids, action: 'status', status: value }, { onSuccess: clear });
                  }}
                >
                  <option value="">{t('Set status…')}</option>
                  {STATUSES.filter((value) => value !== 'CONVERTED' && value !== 'UNQUALIFIED').map((value) => (
                    <option key={value} value={value}>
                      {LEAD_STATUS_LABELS[value]}
                    </option>
                  ))}
                </Select>
              )}
              <Can permission="crm.sequence.update">
                <button
                  type="button"
                  onClick={() => setEnrolling(ids)}
                  className="inline-flex h-7 items-center gap-1.5 rounded-md border bg-card px-2.5 text-xs font-semibold hover:bg-accent"
                >
                  <Workflow className="h-3.5 w-3.5" />
                  {t('Enroll in sequence')}
                </button>
              </Can>
              <BulkExport
                rows={rows}
                ids={ids}
                rowKey={(row) => row.id}
                filename="leads"
                fields={[
                  { header: 'Name', value: (row) => row.name },
                  { header: 'Company', value: (row) => row.companyName },
                  { header: 'Email', value: (row) => row.email },
                  { header: 'Phone', value: (row) => row.phone },
                  { header: 'Status', value: (row) => LEAD_STATUS_LABELS[row.status] },
                  { header: 'Source', value: (row) => LEAD_SOURCE_LABELS[row.source] },
                  { header: 'Score', value: (row) => row.score },
                  { header: 'Owner', value: (row) => row.owner?.name },
                ]}
              />
              {can('crm.lead.delete') && (
                <BulkDelete ids={ids} onDone={clear} remove={(id) => remove.mutateAsync(id)} noun="leads" />
              )}
            </>
          )}
          emptyTitle="No leads yet"
          emptyDescription="Capture your first enquiry to get the pipeline moving."
          emptyAction={
            <Can permission="crm.lead.create">
              <Button onClick={() => setCreating(true)}>
                <UserPlus className="h-4 w-4" />
                {t('Add lead')}
              </Button>
            </Can>
          }
        />
      </ObjectIndex>

      {(creating || editing) && (
        <LeadDialog
          lead={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {converting && <ConvertLeadDialog lead={converting} onClose={() => setConverting(null)} />}
      {enrolling && (
        <EnrollDialog
          targets={enrolling.map((entityId) => ({ entityType: 'lead' as const, entityId }))}
          onClose={() => setEnrolling(null)}
        />
      )}
    </div>
  );
}
